import React, { useState, useRef } from 'react';
import { 
  Building2, 
  Mail, 
  Phone, 
  MapPin, 
  Globe, 
  ImageIcon, 
  Save, 
  Camera,
  LayoutDashboard,
  CheckCircle2,
  Info,
  ExternalLink,
  ShieldCheck,
  Type,
  Loader2,
  CreditCard,
  Zap,
  Percent,
  SendHorizontal,
  Settings as SettingsIcon,
  ChevronRight,
  Database,
  Building,
  AtSign,
  Smartphone,
  Calculator,
  Palette,
  Layout,
  Layers,
  FileText
} from 'lucide-react';
import { BusinessProfile } from '../types';
import { supabase, toSnakeCase, getErrorMessage } from '../lib/supabase';

interface SettingsProps {
  businessProfile: BusinessProfile;
  setBusinessProfile: React.Dispatch<React.SetStateAction<BusinessProfile>>;
}

const SectionHeader = ({ icon: Icon, title, color = "blue" }: any) => {
  let colorClass = "text-blue-600";
  if (color === "indigo") colorClass = "text-indigo-600";
  if (color === "emerald") colorClass = "text-emerald-600";
  if (color === "amber") colorClass = "text-amber-600";
  if (color === "olive") colorClass = "text-[#606c38]";
  
  return (
    <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-3">
      <div className={`${colorClass} p-2 bg-slate-50 rounded-xl`}><Icon size={16} strokeWidth={2} /></div>
      <h4 className="text-[10px] font-bold text-slate-600 uppercase tracking-[0.15em]">{title}</h4>
    </div>
  );
};

const SettingsView: React.FC<SettingsProps> = ({ businessProfile, setBusinessProfile }) => {
  const [formData, setFormData] = useState<BusinessProfile>(businessProfile);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: { user } } = await (supabase.auth as any).getUser();
      if (!user) throw new Error("Authentication failed");

      const payload = toSnakeCase(formData);
      payload.id = user.id;

      const { error } = await supabase.from('business_profiles').upsert(payload);
      if (error) throw error;

      setBusinessProfile(formData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      console.error(err);
      alert(`Sync Error: ${getErrorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setFormData(prev => ({ ...prev, logo: reader.result as string }));
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="animate-in fade-in duration-500 space-y-6 pb-20">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-4 rounded-2xl border border-slate-200 shadow-sm gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-slate-100 rounded-xl text-slate-400">
            <SettingsIcon size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-slate-800">System Configuration</h3>
              {saveSuccess && (
                <span className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-md font-black text-[8px] uppercase tracking-tighter animate-in fade-in zoom-in-95">
                  <CheckCircle2 size={10} /> Synced
                </span>
              )}
            </div>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">Registry Control Panel</p>
          </div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button 
            disabled={saving} 
            onClick={handleSave}
            className="flex-1 sm:flex-none px-6 py-2.5 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-lg active:scale-[0.98] disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} 
            Deploy Changes
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 space-y-8">
          {/* Identity & Branding Card */}
          <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="p-6 md:p-8">
              <SectionHeader icon={Building} title="Organization Identity" color="blue" />
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                <div className="md:col-span-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-3">Corporate Mark</label>
                  <div 
                    onClick={() => fileInputRef.current?.click()} 
                    className="aspect-square bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center cursor-pointer group relative overflow-hidden hover:bg-slate-100 transition-all"
                  >
                    {formData.logo ? (
                      <img src={formData.logo} className="w-full h-full object-contain p-6" alt="Logo" />
                    ) : (
                      <div className="text-center p-4">
                        <Camera size={24} className="mx-auto mb-2 text-slate-300" />
                        <span className="text-[9px] font-black uppercase text-slate-400 tracking-tighter">Upload Logo</span>
                      </div>
                    )}
                    <input ref={fileInputRef} type="file" onChange={handleLogoUpload} accept="image/*" className="hidden" />
                  </div>
                </div>
                <div className="md:col-span-3 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Interface Name</label>
                      <input 
                        required 
                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm focus:bg-white focus:ring-1 focus:ring-blue-500 outline-none transition-all" 
                        value={formData.appName} 
                        onChange={(e)=>setFormData({...formData, appName: e.target.value})} 
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Legal Entity Title</label>
                      <input 
                        required 
                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm focus:bg-white focus:ring-1 focus:ring-blue-500 outline-none transition-all" 
                        value={formData.companyName} 
                        onChange={(e)=>setFormData({...formData, companyName: e.target.value})} 
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Corporate Slogan</label>
                    <input 
                      className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm focus:bg-white focus:ring-1 focus:ring-blue-500 outline-none transition-all" 
                      value={formData.tagline || ''} 
                      onChange={(e)=>setFormData({...formData, tagline: e.target.value})} 
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* NEW REPORT AESTHETICS SECTION */}
          <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="p-6 md:p-8">
              <SectionHeader icon={FileText} title="Report Aesthetics Registry" color="olive" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="space-y-6">
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Protocol Branding Color</label>
                    <div className="flex gap-4 items-center">
                      <input 
                        type="color" 
                        className="w-14 h-14 rounded-xl cursor-pointer bg-transparent border-none p-0" 
                        value={formData.reportColor || '#606c38'} 
                        onChange={(e) => setFormData({...formData, reportColor: e.target.value})} 
                      />
                      <div className="flex-1">
                        <input 
                          className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl font-mono text-xs font-bold text-slate-600 uppercase" 
                          value={formData.reportColor || '#606c38'} 
                          onChange={(e) => setFormData({...formData, reportColor: e.target.value})} 
                        />
                        <p className="text-[8px] font-bold text-slate-400 uppercase mt-1">Used for technical section headers.</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Header Style</label>
                       <select className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold" value={formData.reportHeaderStyle || 'Clean'} onChange={(e) => setFormData({...formData, reportHeaderStyle: e.target.value as any})}>
                          <option value="Clean">Minimal Blueprint</option>
                          <option value="Contrast">High-Impact Contrast</option>
                       </select>
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Geometric Logic</label>
                       <select className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold" value={formData.reportRounding || 'Sharp'} onChange={(e) => setFormData({...formData, reportRounding: e.target.value as any})}>
                          <option value="Sharp">Architectural (Sharp)</option>
                          <option value="Soft">Organic (Soft)</option>
                       </select>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 space-y-6">
                  <div className="flex items-center justify-between">
                     <div className="flex items-center gap-3">
                        <div className="p-2 bg-white rounded-lg text-blue-600 shadow-sm"><Info size={16} /></div>
                        <div>
                           <p className="text-xs font-black text-slate-800 uppercase tracking-tight">Executive Dashboard</p>
                           <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Statistical finding summary</p>
                        </div>
                     </div>
                     <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" checked={formData.showExecutiveSummary !== false} onChange={(e) => setFormData({...formData, showExecutiveSummary: e.target.checked})} />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#606c38]"></div>
                     </label>
                  </div>
                  <div className="h-px bg-slate-200" />
                  <p className="text-[10px] text-slate-500 font-medium leading-relaxed italic">
                    Note: "Architectural Sharp" rounding provides an industrial aesthetic suitable for formal technical engineering reports.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="space-y-6">
          <div className="bg-slate-900 rounded-2xl p-8 text-white shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 p-6 text-white/5 -rotate-12 pointer-events-none">
              <Percent size={120} />
            </div>
            <h4 className="text-[10px] font-bold text-blue-400 uppercase tracking-widest mb-6 relative z-10 flex items-center gap-2"><Calculator size={14} /> Global Registry Logic</h4>
            <div className="space-y-6 relative z-10">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Standard Tax Rate (%)</label>
                <div className="relative">
                  <Percent className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                  <input 
                    type="number"
                    step="0.01"
                    className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/10 rounded-2xl font-black text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all text-xl"
                    value={formData.defaultTaxRate}
                    onChange={(e) => setFormData({...formData, defaultTaxRate: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsView;